import { useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, CATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { UnsubscribeModal } from "./UnsubscribeModal";

// Inverted footer: black plane, white type, red markers. The one heavy block
// on an otherwise light page, anchoring the grid at the bottom.
export function Footer() {
    const brand = useBrand();
    const [unsubOpen, setUnsubOpen] = useState(false);

    return (
        <footer className="mt-24 bg-foreground text-canvas" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                    {/* Brand / contact */}
                    <div className="col-span-2 lg:col-span-1">
                        <div className="flex items-center gap-3">
                            <span className="w-5 h-5 bg-accent shrink-0" />
                            <span className="font-display text-xl font-black uppercase tracking-[-0.03em] leading-none">
                                {brand.site_name}
                            </span>
                        </div>
                        {brand.tagline && (
                            <p className="mt-5 text-sm text-canvas/60 leading-relaxed max-w-xs">{brand.tagline}</p>
                        )}
                        <p className="mt-5 font-mono text-xs text-canvas/50 uppercase tracking-wider" data-testid="footer-contact-email">
                            {brand.email}
                        </p>
                        {brand.phone && (
                            <p className="mt-1.5 font-mono text-xs text-canvas/50 uppercase tracking-wider">{brand.phone}</p>
                        )}
                    </div>

                    {/* Sections */}
                    <div>
                        <div className="h-0.5 bg-accent w-full mb-4" />
                        <h5 className="eyebrow text-canvas/50 mb-4">Sections</h5>
                        <ul className="space-y-2.5">
                            {CATEGORIES.map((c) => (
                                <li key={c}>
                                    <Link to={`/category/${c}`} className="text-sm text-canvas/85 hover:text-accent transition-colors duration-100">
                                        {CATEGORY_LABELS[c]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <div className="h-0.5 bg-accent w-full mb-4" />
                        <h5 className="eyebrow text-canvas/50 mb-4">Company</h5>
                        <ul className="space-y-2.5">
                            <li><Link to="/advertise" data-testid="footer-advertise" className="text-sm text-canvas/85 hover:text-accent transition-colors duration-100">Advertise with us</Link></li>
                            <li><Link to="/archive" className="text-sm text-canvas/85 hover:text-accent transition-colors duration-100">Archive</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className="text-sm text-canvas/85 hover:text-accent transition-colors duration-100">Privacy Policy</Link></li>
                            <li><Link to="/terms" data-testid="footer-terms" className="text-sm text-canvas/85 hover:text-accent transition-colors duration-100">Terms of Use</Link></li>
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div>
                        <div className="h-0.5 bg-accent w-full mb-4" />
                        <h5 className="eyebrow text-canvas/50 mb-4" data-testid="footer-newsletter-title">Newsletter</h5>
                        <p className="text-sm text-canvas/60 leading-relaxed">
                            Get the latest financial news delivered to your inbox.
                        </p>
                        <Link
                            to="/register"
                            data-testid="footer-newsletter-subscribe"
                            className="mt-5 inline-block eyebrow bg-accent text-accent-foreground px-6 py-3 hover:bg-canvas hover:text-foreground transition-colors duration-100"
                        >
                            Subscribe
                        </Link>
                        <button
                            onClick={() => setUnsubOpen(true)}
                            data-testid="footer-unsubscribe"
                            className="mt-4 block text-xs text-canvas/45 hover:text-accent transition-colors duration-100"
                        >
                            Unsubscribe
                        </button>
                    </div>
                </div>

                <p className="border-t border-canvas/20 mt-12 pt-6 font-mono text-[11px] text-canvas/40 uppercase tracking-wider">
                    © {new Date().getFullYear()} {brand.site_name} · {brand.domain}
                </p>
            </div>
            <UnsubscribeModal open={unsubOpen} onOpenChange={setUnsubOpen} />
        </footer>
    );
}
